import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  Button,
  CircularProgress,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaidIcon from '@mui/icons-material/Paid';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ViewListIcon from '@mui/icons-material/ViewList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import { formatCurrency } from '../../../utils/mortgageUtils';
import { StatBox } from '../UserSettingsStyles';
import MortgageTracksList from './MortgageTracksList';
import UploadDocumentCard from './UploadDocumentCard';

const MortgageView = ({ 
  formData, 
  banks, 
  trackTypes, 
  calculateWeightedInterest, 
  onSetupMortgage,
  isLoading,
  error
}) => {
  const [showExplanation, setShowExplanation] = React.useState(false);
  
  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle 404 or no mortgage data
  if (error?.status === 404 || !formData || !formData.mortgageTracks || formData.mortgageTracks.length === 0) {
    return (
      <Card
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: '12px',
          background: 'linear-gradient(120deg, #f5f5f5 30%, #ffffff 90%)',
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            margin: '0 auto 24px',
            bgcolor: '#1976d2',
          }}
        >
          <AccountBalanceIcon sx={{ fontSize: 40 }} />
        </Avatar>
        
        <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
          הגדרת משכנתא חדשה
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
          {error?.status === 404 
            ? 'לא נמצאה משכנתא במערכת. כדי להתחיל לנהל את המשכנתא שלך, תוכל להגדיר אותה כעת.'
            : 'נראה שעדיין לא הגדרת את המשכנתא שלך במערכת. כדי להתחיל לנהל את המשכנתא שלך, תוכל להגדיר אותה כעת.'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={onSetupMortgage}
            sx={{ minWidth: 200 }}
          >
            הגדרת משכנתא
          </Button>
        </Box>

        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', bgcolor: 'rgba(25, 118, 210, 0.05)' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                ניהול מסלולים
              </Typography>
              <Typography variant="body2" color="text.secondary">
                הגדר את המסלולים השונים של המשכנתא שלך, כולל סכומים, ריביות ותקופות
              </Typography>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', bgcolor: 'rgba(25, 118, 210, 0.05)' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                מעקב תשלומים
              </Typography>
              <Typography variant="body2" color="text.secondary">
                עקוב אחר התשלומים החודשיים והריביות המשוקללות של המשכנתא שלך
              </Typography>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', bgcolor: 'rgba(25, 118, 210, 0.05)' }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                ניהול מסמכים
              </Typography>
              <Typography variant="body2" color="text.secondary">
                העלה ונהל את המסמכים הקשורים למשכנתא שלך במקום אחד מרוכז
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Card>
    );
  }

  // Handle other errors
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error" gutterBottom>
          שגיאה בטעינת נתוני המשכנתא
        </Typography>
        <Typography variant="body2" color="text.secondary">
          אנא נסה שוב מאוחר יותר
        </Typography>
      </Box>
    );
  }

  const bankObj = banks.find(b => b.name === formData.bank);

  return (
    <Box>
      {/* Main Mortgage Summary Card */}
      <Card 
        elevation={3} 
        sx={{ 
          mb: 4, 
          background: 'linear-gradient(120deg, #1976d2 30%, #2196f3 90%)',
          color: 'white',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'white' }}>
              <AccountBalanceIcon sx={{ color: '#1976d2' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                {bankObj?.name || 'משכנתא'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                  {formData.startDate && 
                    `תאריך התחלה: ${new Date(formData.startDate).toLocaleDateString('he-IL')} | תקופה: ${formData.loanPeriod} שנים`
                  }
                </Typography>
                <Tooltip 
                  title="תקופת ההלוואה נקבעת לפי המסלול עם התקופה הארוכה ביותר"
                  placement="top"
                >
                  <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.8)', ml: 0.5, p: 0 }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip 
              title="הריבית המשוקללת מחושבת לפי סכום כל מסלול כפול הריבית שלו, מחולק בסכום ההלוואה הכולל"
              placement="top"
            >
              <Chip 
                label={`ריבית משוקללת: ${calculateWeightedInterest()}%`} 
                color="secondary"
                sx={{ fontWeight: 'bold' }}
              />
            </Tooltip>
          </Box>
        </Box>

        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Tooltip 
                title="סכום ההלוואה הכולל מחושב כסכום של כל המסלולים יחד"
                placement="top"
              >
                <StatBox>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <PaidIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(formData.loanAmount)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="rgba(255,255,255,0.8)">
                    סכום ההלוואה
                  </Typography>
                </StatBox>
              </Tooltip>
            </Grid>

            <Grid item xs={12} md={4}>
              <Tooltip 
                title="התשלום החודשי הכולל מחושב כסכום של התשלומים החודשיים של כל המסלולים"
                placement="top"
              >
                <StatBox>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <CreditCardIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(formData.monthlyPayment)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="rgba(255,255,255,0.8)">
                    תשלום חודשי
                  </Typography>
                </StatBox>
              </Tooltip>
            </Grid>

            <Grid item xs={12} md={4}>
              <StatBox>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <ViewListIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
                  <Typography variant="h5" fontWeight="bold">
                    {formData.mortgageTracks.length} מסלולים
                  </Typography>
                </Box>
                <Typography variant="body2" color="rgba(255,255,255,0.8)">
                  מספר מסלולים
                </Typography>
              </StatBox>
            </Grid>
          </Grid>

          {/* Calculation Explanation Section */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <IconButton 
              onClick={() => setShowExplanation(!showExplanation)}
              sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <InfoOutlinedIcon />
              <Typography variant="body2" sx={{ ml: 1 }}>
                איך מחושבים הנתונים?
              </Typography>
            </IconButton>
            
            <Collapse in={showExplanation}>
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'rgba(255,255,255,0.1)', 
                borderRadius: '8px',
                textAlign: 'right'
              }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • סכום ההלוואה הכולל: חיבור של סכומי כל המסלולים
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • תשלום חודשי כולל: חיבור של התשלומים החודשיים של כל המסלולים
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • תקופת ההלוואה: נקבעת לפי המסלול עם התקופה הארוכה ביותר
                </Typography>
                <Typography variant="body2">
                  • ריבית משוקללת: מחושבת לפי (סכום כל מסלול × הריבית שלו) ÷ סכום ההלוואה הכולל
                </Typography>
              </Box>
            </Collapse>
          </Box>
        </CardContent>
      </Card>

      <UploadDocumentCard />

      {/* Tracks List */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ 
          mb: 3, 
          display: 'flex', 
          alignItems: 'center',
          color: '#1976d2',
          fontWeight: 'bold'
        }}>
          <ViewListIcon sx={{ mr: 1 }} />
          מסלולי משכנתא
        </Typography>
        <MortgageTracksList 
          tracks={formData.mortgageTracks.map(track => ({
            ...track,
            type: track.type,
            amount: track.amount,
            baseInterest: track.current_base_interest,
            margin: track.addition_rate,
            monthlyPayment: track.monthly_payment,
            period: track.period
          }))} 
        />
      </Box>
    </Box>
  );
};

export default MortgageView; 